import React, { useEffect } from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PaginationControls from "./PaginationControls";
import { Notifications } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getNewsList } from "../../../../features/user/userSlice";
import { timeAgo } from "../../../../utils/timeAgo";
import NewsCard from "./NewsCard";

const NewsTab = ({ country, page, setPage, entriesPerPage = 6 }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { newsData, newsLoading } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(getNewsList()).unwrap();
  }, [dispatch]);

  const newsFromApi = newsData?.articles || [];

  useEffect(() => {
    if (newsData?.articles?.length) {
      setPage(1);
    }
  }, []); // run only on first load

  const mappedNews = newsFromApi.map((item) => ({
    title: item.title,
    summary: item.description,
    time: timeAgo(item.published_at),
    url: item.url,
    image: item.image,
    color: theme.palette.info.main,
  }));

  const totalPages = Math.ceil(mappedNews.length / entriesPerPage);

  const paginatedNews = mappedNews.slice(
    (page - 1) * entriesPerPage,
    page * entriesPerPage
  );

  // === LOADING BLOCK ===
  if (newsLoading) {
    return (
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.warning.main,
              0.08
            )} 0%, ${alpha(theme.palette.error.main, 0.08)} 100%)`,
          }}
        >
          <Notifications
            sx={{ fontSize: 32, color: theme.palette.warning.main }}
          />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Market News
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {country} Market • Latest updates and insights
            </Typography>
          </Box>
        </Box>

        {/* Empty State */}
        {paginatedNews.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              color: theme.palette.text.secondary,
              fontStyle: "italic",
            }}
          >
            No news available for {country}.
          </Box>
        ) : (
          <>
            {/* GRID: Now using fixed width for two columns */}
            <Box sx={{ width: "100%", display: "block" }}>
              <Grid
                container
                spacing={3}
                alignItems="stretch"
                sx={{
                  width: "100%",
                  mx: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {paginatedNews.map((n, i) => (
                  <Grid
                    item
                    xs={12}
                    // REMOVED sm={6}
                    key={`${n.title}-${i}`}
                    sx={{
                      // FORCES FIXED WIDTH on small screens (sm) and up
                      flexBasis: { sm: "580px" },
                      maxWidth: { sm: "580px" },
                      display: "flex",
                      height: "100%",
                    }}
                  >
                    <NewsCard article={n} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Pagination */}
            <PaginationControls
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </>
        )}
      </Box>
    );
  }

  // === MAIN RETURN BLOCK ===
  return (
    // FIX 1: Max width applied to main return wrapper
    <Box sx={{ width: "100%", mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.warning.main,
            0.08
          )} 0%, ${alpha(theme.palette.error.main, 0.08)} 100%)`,
        }}
      >
        <Notifications
          sx={{ fontSize: 32, color: theme.palette.warning.main }}
        />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Market News
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {country} Market • Latest updates and insights
          </Typography>
        </Box>
      </Box>

      {/* Empty State */}
      {paginatedNews.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            color: theme.palette.text.secondary,
            fontStyle: "italic",
          }}
        >
          No news available for {country}.
        </Box>
      ) : (
        <>
          {/* ONE CONSISTENT CARD DESIGN */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center", // centers horizontally
              alignItems: "flex-start", // optional, keeps vertical alignment top
              width: "100%",
            }}
          >
            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="stretch" // Critical for uniform height
              sx={{
                maxWidth: "1400px", // control grid max width (adjust as needed)
                width: "100%",
                mx: "auto",
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              {paginatedNews.map((n, i) => (
                <Grid
                  item
                  xs={12}
                  // REMOVED sm={6}
                  key={`${n.title}-${i}`}
                  sx={{
                    // FORCES FIXED WIDTH on small screens (sm) and up
                    flexBasis: { sm: "705px", md: "665px" },
                    maxWidth: { sm: "705px", md: "665px" },
                    display: "flex",
                    height: "100%",
                  }}
                >
                  <NewsCard article={n} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <PaginationControls
            page={page}
            setPage={setPage}
            totalPages={totalPages}
          />
        </>
      )}
    </Box>
  );
};

export default NewsTab;
